<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['cancel'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: '',
      selectedType: '',
      isSubmitting: false,
    };
  },
  validations: {
    email: {
      required,
      email,
      minLength: minLength(4),
    },
  },
  computed: {
    // Cada escolha mostra O ENDEREÇO para onde vai. Eram três frases
    // ("Envie a transcrição para o cliente") sem dizer qual e-mail — quem
    // enviava só descobria o destino depois, na caixa de alguém.
    opcoes() {
      const { sender, assignee } = this.currentChat.meta || {};
      return [
        sender?.email && {
          valor: 'contact',
          icone: 'i-lucide-user-round',
          rotulo: this.$t('EMAIL_TRANSCRIPT.FORM.SEND_TO_CONTACT'),
          nota: sender.email,
        },
        assignee?.email && {
          valor: 'assignee',
          icone: 'i-lucide-headset',
          rotulo: this.$t('EMAIL_TRANSCRIPT.FORM.SEND_TO_AGENT'),
          nota: assignee.email,
        },
        {
          valor: 'other_email_address',
          icone: 'i-lucide-at-sign',
          rotulo: this.$t('EMAIL_TRANSCRIPT.FORM.SEND_TO_OTHER_EMAIL_ADDRESS'),
          nota: this.$t('EMAIL_TRANSCRIPT.FORM.OTHER_NOTE'),
        },
      ].filter(Boolean);
    },
    sentToOtherEmailAddress() {
      return this.selectedType === 'other_email_address';
    },
    isFormValid() {
      if (!this.selectedType) return false;
      if (!this.sentToOtherEmailAddress) return true;
      return !!this.email && !this.v$.email.$invalid;
    },
    selectedEmailAddress() {
      if (this.sentToOtherEmailAddress) return this.email.trim();
      return this.opcoes.find(o => o.valor === this.selectedType)?.nota || '';
    },
  },
  // Fica montado (o pai não usa mais `v-if`, para a saída aparecer): cada abertura começa
  // limpa, como quando o modal nascia a cada vez (24/10/2026).
  watch: {
    show(aberto) {
      if (!aberto) return;
      this.email = '';
      this.selectedType = '';
      this.v$?.$reset?.();
    },
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    escolher(valor) {
      this.selectedType = valor;
      if (valor !== 'other_email_address') return;
      this.$nextTick(() => this.$refs.campoEmail?.focus());
    },
    async onSubmit() {
      // 🔴 Era `isSubmitting = false` aqui e o botão não olhava a variável:
      // um segundo clique durante o envio mandava a transcrição de novo.
      if (!this.isFormValid || this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        await this.$store.dispatch('sendEmailTranscript', {
          email: this.selectedEmailAddress,
          conversationId: this.currentChat.id,
        });
        useAlert(this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'));
        this.onCancel();
      } catch (error) {
        const status = error?.response?.status;
        if (status === 402) {
          useAlert(this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_PAYMENT_REQUIRED'));
        } else {
          useAlert(this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'));
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="advup-saida">
      <div v-if="show" class="advup-modal-fundo" @click.self="onCancel">
        <form
          v-modal-acessivel="onCancel"
          class="advup-modal advup-modal--claro advup-modal--estreito advup-modal--teto"
          @submit.prevent="onSubmit"
        >
          <div class="advup-modal__topo">
            <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
                 degradê escuro (05/10/2026). -->
            <i class="advup-modal__marca i-lucide-mail" aria-hidden="true" />
            <span class="advup-modal__selo" aria-hidden="true">
              <i class="i-lucide-mail" />
            </span>
            <div class="min-w-0 flex-1">
              <h2 class="advup-modal__titulo">
                {{ $t('EMAIL_TRANSCRIPT.TITLE') }}
              </h2>
              <p class="advup-modal__sub advup-modal__sub--solta">
                {{ $t('EMAIL_TRANSCRIPT.DESC') }}
              </p>
            </div>
            <button
              type="button"
              class="advup-modal__fechar"
              :aria-label="$t('EMAIL_TRANSCRIPT.CANCEL')"
              @click="onCancel"
            >
              <i class="i-lucide-x" />
            </button>
          </div>

          <div class="advup-modal__corpo etm-corpo">
            <p id="etm-para" class="advup-rotulo advup-rotulo--secao etm-rot">
              {{ $t('EMAIL_TRANSCRIPT.FORM.RECIPIENT') }}
            </p>
            <div class="etm-lista" role="radiogroup" aria-labelledby="etm-para">
              <button
                v-for="opcao in opcoes"
                :key="opcao.valor"
                type="button"
                role="radio"
                class="advup-escolha etm-escolha"
                :class="{ 'advup-escolha--on': selectedType === opcao.valor }"
                :aria-checked="String(selectedType === opcao.valor)"
                @click="escolher(opcao.valor)"
              >
                <span class="advup-escolha__ico" aria-hidden="true">
                  <i :class="opcao.icone" />
                </span>
                <span class="etm-escolha__txt">
                  <span class="advup-escolha__rot">{{ opcao.rotulo }}</span>
                  <span class="advup-escolha__nota etm-escolha__nota">
                    {{ opcao.nota }}
                  </span>
                </span>
              </button>
            </div>

            <div v-if="sentToOtherEmailAddress" class="etm-outro">
              <label for="etm-email" class="advup-rotulo">
                {{ $t('EMAIL_TRANSCRIPT.FORM.EMAIL.LABEL') }}
              </label>
              <input
                id="etm-email"
                ref="campoEmail"
                v-model="email"
                class="advup-campo reset-base"
                :class="{ 'advup-campo--erro': v$.email.$error }"
                type="email"
                autocomplete="email"
                :aria-invalid="v$.email.$error"
                :aria-describedby="v$.email.$error ? 'etm-erro' : undefined"
                :placeholder="$t('EMAIL_TRANSCRIPT.FORM.EMAIL.PLACEHOLDER')"
                @blur="v$.email.$touch"
              />
              <p
                v-if="v$.email.$error"
                id="etm-erro"
                class="advup-nota advup-nota--erro"
              >
                {{ $t('EMAIL_TRANSCRIPT.FORM.EMAIL.ERROR') }}
              </p>
            </div>
          </div>

          <div class="advup-modal__rodape advup-modal__rodape--acoes">
            <button type="button" class="advup-btn" @click="onCancel">
              {{ $t('EMAIL_TRANSCRIPT.CANCEL') }}
            </button>
            <button
              type="submit"
              class="advup-btn-forte"
              :disabled="!isFormValid || isSubmitting"
            >
              <i
                :class="
                  isSubmitting
                    ? 'i-lucide-loader-2 advup-gira'
                    : 'i-lucide-send'
                "
                aria-hidden="true"
              />
              {{
                isSubmitting
                  ? $t('EMAIL_TRANSCRIPT.SENDING')
                  : $t('EMAIL_TRANSCRIPT.SUBMIT')
              }}
            </button>
          </div>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* A escolha do destinatário é a peça `.advup-escolha` deitada: ícone à
   esquerda e, ao lado, o nome da escolha com o endereço embaixo. */
.etm-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

.etm-rot {
  margin: 0;
}

.etm-lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.etm-escolha {
  flex-direction: row;
  align-items: center;
  width: 100%;
}

.etm-escolha__txt {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.etm-escolha__nota {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.etm-outro {
  display: flex;
  flex-direction: column;
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.etm-outro > .advup-nota--erro {
  margin-top: var(--esp-1);
}

@media (prefers-reduced-motion: reduce) {
  .etm-outro {
    animation: none;
  }
}
</style>
